import React from 'react';
import {
  DocPage, PageHeader, Section,
  Para, Callout, Prerequisites, NextStep,
} from '../components/DocBlocks';
import { useIndustry } from '../IndustryContext';
import './RateCardWildcard.css';

const TOC = [
  { id: 'overview',    label: 'Overview' },
  { id: 'migration',   label: 'How "Any" is created' },
  { id: 'lookup',      label: 'Lookup and precedence' },
  { id: 'grid',        label: 'How it looks in the grid' },
  { id: 'override',    label: 'Creating a specific override' },
  { id: 'invariants',  label: 'Invariants' },
];

// ── Chip ──────────────────────────────────────────────────────────────────────

function Chip({ label, isAny }) {
  return <span className={`wc-chip${isAny ? ' wc-chip--any' : ''}`}>{label}</span>;
}

// ── Migration diagram ─────────────────────────────────────────────────────────

function MigrationDiagram({ migration }) {
  const { beforeDims, afterDims, newDim, cards } = migration;
  return (
    <div className="wc-migration">
      {/* Before */}
      <div className="wc-migration-panel">
        <div className="wc-migration-header">
          <span className="wc-migration-title">Before — {beforeDims.join(' only')}</span>
          <span className="wc-migration-badge wc-migration-badge--before">{cards.length} cards</span>
        </div>
        {cards.map((card) => (
          <div key={card.id} className="wc-migration-row">
            {card.before.map((v) => (
              <span key={v} className="wc-migration-chip">{v}</span>
            ))}
            <span className="wc-migration-rate">{card.rate}</span>
          </div>
        ))}
      </div>

      <div className="wc-migration-arrow">→</div>

      {/* After */}
      <div className="wc-migration-panel">
        <div className="wc-migration-header">
          <span className="wc-migration-title">After — {newDim} added</span>
          <span className="wc-migration-badge wc-migration-badge--after">{cards.length} cards, migrated</span>
        </div>
        {cards.map((card) => (
          <div key={card.id} className="wc-migration-row">
            {card.before.map((v) => (
              <span key={v} className="wc-migration-chip">{v}</span>
            ))}
            <span className="wc-migration-chip wc-migration-chip--new wc-migration-chip--any" style={{ borderStyle: 'dashed' }}>
              Any {newDim}
            </span>
            <span className="wc-migration-rate">{card.rate}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Lookup table ──────────────────────────────────────────────────────────────

function LookupTable({ lookup }) {
  const { cards, queries } = lookup;
  return (
    <div className="wc-lookup">
      <div className="wc-lookup-cards">
        {cards.map((card) => (
          <div key={card.id} className="wc-lookup-card">
            <span className="wc-lookup-card-label">Card</span>
            <div className="wc-lookup-card-dims">
              {card.dims.map((d) => (
                <Chip key={d.label} label={d.isAny ? `Any ${d.label}` : `${d.label}: ${d.value}`} isAny={d.isAny} />
              ))}
            </div>
            <span className="wc-lookup-card-rate">{card.rate}</span>
            <span className="wc-lookup-card-covers">{card.covers}</span>
          </div>
        ))}
      </div>
      <div className="wc-lookup-queries">
        {queries.map((q, i) => (
          <div key={i} className="wc-lookup-query">
            <span className="wc-lookup-query-label">Query</span>
            <div className="wc-lookup-query-dims">
              {q.dims.map((d) => (
                <Chip key={d.label} label={`${d.label}: ${d.value}`} />
              ))}
            </div>
            <div className="wc-lookup-query-result">
              <span className="wc-lookup-query-rate">{q.rate}</span>
              <span className={`wc-lookup-query-reason wc-lookup-query-reason--${q.matchType}`}>{q.reason}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Grid preview ──────────────────────────────────────────────────────────────

function GridPreview({ grid }) {
  const { dims, rows } = grid;
  const colCount = dims.length + 1;
  const gridCols = `repeat(${dims.length}, 1fr) 120px`;

  return (
    <div className="wc-grid">
      <div className="wc-grid-header" style={{ gridTemplateColumns: gridCols }}>
        {dims.map((d) => (
          <div key={d} className="wc-grid-th">{d}</div>
        ))}
        <div className="wc-grid-th">Rate</div>
      </div>
      {rows.map((row, i) => (
        <div key={i} className={`wc-grid-row${row.isWildcard ? ' wc-grid-row--wildcard' : ''}`} style={{ gridTemplateColumns: gridCols }}>
          {row.cells.map((cell, j) => (
            <div key={j} className="wc-grid-cell">
              <Chip label={cell.isAny ? 'Any' : cell.value} isAny={cell.isAny} />
            </div>
          ))}
          <div className={`wc-grid-cell${row.unconfigured ? ' wc-grid-cell--unconfigured' : ' wc-grid-cell--rate'}`}>
            {row.unconfigured ? 'Not configured' : row.rate}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RateCardWildcard({ onNavigate }) {
  const { industry } = useIndustry();
  const wc = industry.rateCardWildcard;

  return (
    <DocPage toc={TOC}>
      <PageHeader
        eyebrow="Rate Cards"
        title="Wildcard Dimension Values"
        description={'A rate card\'s dimension value can be null — meaning it applies to any slot on that dimension. This is the wildcard, shown in the UI as “Any”.'}
      />

      <Prerequisites
        onNavigate={onNavigate}
        items={[
          { id: 'pricing-dimensions', label: 'Pricing Dimensions' },
          { id: 'rate-card',          label: 'Rate Cards' },
        ]}
      />

      <Section id="overview" title="Overview">
        <Para>
          Every rate card stores a map of dimension values — the specific combination it prices.
          For example, a card might carry{' '}
          <code style={{ fontSize: 13 }}>{`{ ${wc.exampleBefore} }`}</code>.
          Each key is a dimension, each value is a slot within that dimension.
        </Para>
        <Para>
          A value in that map can also be <strong>null</strong>. A null means "this rate card
          applies to any slot on this dimension". In the grid UI this appears as an{' '}
          <span className="wc-chip wc-chip--any" style={{ verticalAlign: 'middle' }}>Any</span>{' '}
          chip. In the system it acts as a wildcard: the card matches any query regardless
          of what the queried slot is for that dimension.
        </Para>
        <Callout type="info" title="Any is not a gap — it is a valid, billable rate card">
          A wildcard card is fully configured and will be used for billing. It is not a
          placeholder or a sign that setup is incomplete. It means one rate applies
          across all values of that dimension.
        </Callout>
      </Section>

      <Section id="migration" title='How "Any" is created'>
        <Para>
          Users never set a dimension value to "Any" manually. The wildcard only appears
          as a result of a <strong>dimension-level change</strong> on a product:
        </Para>
        <Para>
          <strong>Adding a new dimension to a product that already has rate cards.</strong>{' '}
          The new dimension is added to every existing rate card for that product, with
          its value set to null. This preserves all existing pricing — the cards keep
          working, now also matching any value of the new dimension.
        </Para>
        <Para>
          In the example below, {wc.migration.cards.length} rate cards exist for{' '}
          {wc.migration.beforeDims.join(' and ')}. When the dimension{' '}
          {wc.migration.newDim} is added, both cards are automatically migrated:
        </Para>
        <MigrationDiagram migration={wc.migration} />
        <Para>
          The inverse — <strong>removing a dimension</strong> — strips that key from
          every rate card for the product. No nulls are introduced; the dimension
          simply ceases to exist in the map.
        </Para>
        <Callout type="tip" title="No action needed after adding a dimension">
          Existing rate cards continue to work immediately after a dimension is added.
          If you want different prices per value of the new dimension, you can add
          specific cards for each combination — those will override the wildcard for
          their exact slot.
        </Callout>
      </Section>

      <Section id="lookup" title="Lookup and precedence">
        <Para>
          When the billing engine looks up the rate card for a specific slot, a card
          is considered a <strong>match</strong> if, for every dimension, its stored
          value is either the exact queried value or null. Both are valid matches.
        </Para>
        <Para>
          If more than one card matches — which happens when a specific card and a
          wildcard card both cover a slot — the <strong>most specific card wins</strong>.
          Specificity is measured by the number of nulls: the card with fewest nulls
          takes precedence. A fully specific card always overrides a wildcard card.
        </Para>
        <LookupTable lookup={wc.lookup} />
        <Para>
          A wildcard card acts as a fallback: it prices every slot it covers that does
          not have a more specific card configured. You can layer specific cards on top
          of a wildcard without touching the wildcard itself.
        </Para>
      </Section>

      <Section id="grid" title="How it looks in the grid">
        <Para>
          A wildcard card appears as a <strong>single row</strong> in the rate card grid,
          not as one row per cartesian combination it covers. The row's dimension columns
          show{' '}
          <span className="wc-chip wc-chip--any" style={{ verticalAlign: 'middle' }}>Any</span>{' '}
          for dimensions where the card has null. Specific dimension values show their
          normal slot chip.
        </Para>
        <GridPreview grid={wc.grid} />
        <Para>
          Cartesian cells that are not covered by any card — neither a specific card nor
          a wildcard — still render individually as "Not configured". A wildcard covers
          those cells for billing purposes; they show as "Not configured" only when the
          wildcard itself has not been set up for a given dimension combination.
        </Para>
        <Callout type="info" title={'One “Any” row, not N repeated rows'}>
          A single wildcard card with <em>Weight: Any</em> covering 4 weight values
          produces <strong>one row</strong> in the grid. If you see it as four rows, each
          with "Any" repeated, that would be four separate cards — not one wildcard.
        </Callout>
      </Section>

      <Section id="override" title="Creating a specific override">
        <Para>
          Opening a grid cell that is covered by a wildcard and saving a price creates
          a <strong>new specific card</strong> for exactly that slot. The wildcard is left
          intact as a fallback for the remaining slots.
        </Para>
        <Para>
          The system distinguishes between "opening the wildcard row itself" (which edits
          the wildcard in place) and "opening a specific slot covered by the wildcard"
          (which creates a new specific card). The distinction is made by checking
          whether the slot being saved has a concrete value — not null — for the
          dimension where the wildcard had null.
        </Para>
        <Para>
          The same rule applies to <strong>CSV bulk import</strong>: a row with a specific
          value creates a new specific card rather than overwriting the wildcard that
          previously covered that combination.
        </Para>
        <Callout type="warning" title="Delete by ID, not by dimValues">
          When deleting a wildcard card, always delete by the card's ID. Reconstructing
          the dimValues from the cartesian grid slot will produce specific values — not
          nulls — and the deletion will silently fail to match the wildcard card.
        </Callout>
      </Section>

      <Section id="invariants" title="Invariants">
        <Para>
          A few rules always hold for rate card dimension maps:
        </Para>
        <ul style={{ paddingLeft: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <li style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
            Every rate card has <strong>exactly one key per product dimension</strong> at
            any time. Adding or removing a dimension migrates every card for that product
            immediately to preserve this.
          </li>
          <li style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
            A key's value is either a valid slot ID for that dimension, or null.
          </li>
          <li style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
            Null means <em>any slot</em> for lookup purposes. It does not mean "missing",
            "invalid", or "needs attention".
          </li>
          <li style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.65 }}>
            A dimensionless product has <code style={{ fontSize: 12 }}>dimValues: {'{}'}</code>{' '}
            (no keys). A wildcard card has keys whose values happen to be null. These
            are distinct states.
          </li>
        </ul>
      </Section>

      <NextStep
        onNavigate={onNavigate}
        items={[
          { id: 'pricing-dimensions', title: 'Pricing Dimensions', desc: 'How dimensions are defined, grouped, and selected per fee.' },
          { id: 'rate-card',          title: 'Rate Cards',          desc: 'How rate cards are structured, versioned, and selected at billing time.' },
        ]}
      />
    </DocPage>
  );
}
